import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { baseUrl } from "../../config";
import { trackEvent } from "../../components/trackingUtils";
import {
  FaBriefcase,
  FaMapMarkerAlt,
  FaClock,
  FaGraduationCap,
  FaEnvelope,
  FaRocket,
  FaUsers,
  FaLightbulb,
  FaTrophy,
} from "react-icons/fa";

const Careers = () => {
  // Track page load event
  useEffect(() => {
    try {
      trackEvent("PageLoad", "", "Careers"); // Track the page load event
    } catch (error) {
      console.error("Error tracking page load event:", error);
    }
  }, []);

  const title = "Careers at GeniusNexa | Join Our Innovative Technology Team";
  const description =
    "Join GeniusNexa's talented team of developers, data scientists, and tech innovators. Explore exciting career opportunities in AI, cloud computing, and software development. Competitive packages, flexible work culture, and growth opportunities await. Apply now!";
  const keywords =
    "GeniusNexa careers, tech jobs, software developer jobs, data scientist jobs, Python developer, .NET developer, remote jobs, IT jobs India, technology careers, startup jobs, AI jobs, cloud computing careers, job openings, employment opportunities";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${baseUrl}/careers`} />

        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={`${baseUrl}/images/careers.png`} />
        <meta property="og:url" content={`${baseUrl}/careers`} />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${baseUrl}/images/careers.png`} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Careers at GeniusNexa",
            description: description,
            url: `${baseUrl}/careers`,
            mainEntity: {
              "@type": "Organization",
              name: "GeniusNexa",
              url: baseUrl,
              sameAs: [
                "https://www.linkedin.com/company/geniusnexa",
                "https://twitter.com/geniusnexa",
              ],
            },
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-700"></div>
        </div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Build Your Future with{" "}
              <span className="text-yellow-400">GeniusNexa</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Join a team of innovators, creators, and tech enthusiasts shaping
              the future of technology
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
                <div className="text-3xl font-bold text-yellow-400">15+</div>
                <div className="text-sm text-blue-200">Team Members</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
                <div className="text-3xl font-bold text-yellow-400">100%</div>
                <div className="text-sm text-blue-200">Remote Friendly</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
                <div className="text-3xl font-bold text-yellow-400">4.8/5</div>
                <div className="text-sm text-blue-200">Employee Rating</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Join Us Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Why Work With Us?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We believe in empowering our team members to do their best work
            while maintaining work-life balance
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-shadow border border-gray-100">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <FaRocket className="text-3xl text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Innovation First
            </h3>
            <p className="text-gray-600">
              Work on cutting-edge technologies and innovative projects that
              make a real impact
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-shadow border border-gray-100">
            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <FaUsers className="text-3xl text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Collaborative Culture
            </h3>
            <p className="text-gray-600">
              Join a supportive team environment where ideas are valued and
              collaboration thrives
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-shadow border border-gray-100">
            <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <FaLightbulb className="text-3xl text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Continuous Learning
            </h3>
            <p className="text-gray-600">
              Access to courses, certifications, and conferences to keep your
              skills sharp
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-shadow border border-gray-100">
            <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <FaTrophy className="text-3xl text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Competitive Benefits
            </h3>
            <p className="text-gray-600">
              Attractive compensation, health benefits, and flexible working
              arrangements
            </p>
          </div>
        </div>
      </div>

      {/* Job Listings Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Open Positions
          </h2>
          <p className="text-xl text-gray-600">
            Find your perfect role and start your journey with us
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-6">
          <JobPosting
            title=".NET Developer"
            location="Remote / Hybrid"
            type="Full-time"
            experience="2-5 years"
            description="We are looking for a skilled .NET developer to join our team. The ideal candidate will have experience in building scalable web applications using ASP.NET Core, C#, and modern front-end frameworks."
            requirements={[
              "Strong proficiency in ASP.NET Core and C#",
              "Experience with SQL Server, Entity Framework Core, and RESTful APIs",
              "Understanding of modern front-end technologies (React, Angular, or Vue)",
              "Knowledge of microservices architecture and cloud platforms (Azure/AWS)",
              "Excellent problem-solving and communication skills",
            ]}
            onClick={() => trackEvent("JobClick", ".NET Developer")}
          />

          <JobPosting
            title="Python Developer"
            location="Remote / Hybrid"
            type="Full-time"
            experience="2-4 years"
            description="Join our Python development team to build robust web applications, backend services, and data pipelines. Ideal candidates should have experience with Django or FastAPI frameworks."
            requirements={[
              "Proficiency in Python 3.x and Django or FastAPI",
              "Experience with RESTful APIs, ORM libraries, and async programming",
              "Familiarity with PostgreSQL/MySQL and Redis",
              "Understanding of Docker, CI/CD pipelines, and cloud services",
              "Experience with testing frameworks (pytest, unittest)",
            ]}
            onClick={() => trackEvent("JobClick", "Python Developer")}
          />

          <JobPosting
            title="Data Scientist"
            location="Remote / Hybrid"
            type="Full-time"
            experience="3-6 years"
            description="We are seeking a talented Data Scientist to analyze large datasets, develop machine learning models, and derive actionable insights. Candidates should have expertise in statistical analysis and ML frameworks."
            requirements={[
              "Strong knowledge of statistical analysis and machine learning techniques",
              "Proficiency in Python (NumPy, Pandas, Scikit-learn, TensorFlow/PyTorch)",
              "Experience with data visualization tools (Tableau, Power BI, Matplotlib, Plotly)",
              "Familiarity with SQL, big data technologies (Spark, Hadoop)",
              "Excellent analytical thinking and communication skills",
            ]}
            onClick={() => trackEvent("JobClick", "Data Scientist")}
          />

          <JobPosting
            title="React.js Developer"
            location="Remote / Hybrid"
            type="Full-time"
            experience="2-5 years"
            description="Looking for an experienced React.js developer to build modern, responsive web applications with excellent user experience. Knowledge of TypeScript and state management is essential."
            requirements={[
              "Strong proficiency in React.js, JavaScript ES6+, and TypeScript",
              "Experience with state management (Redux, Context API, Zustand)",
              "Understanding of modern CSS frameworks (Tailwind, Material-UI)",
              "Knowledge of build tools (Webpack, Vite) and testing (Jest, React Testing Library)",
              "Familiarity with RESTful APIs and GraphQL",
            ]}
            onClick={() => trackEvent("JobClick", "React.js Developer")}
          />
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-extrabold mb-4">Don't See Your Role?</h2>
          <p className="text-xl mb-8 font-medium text-blue-100">
            We're always looking for talented individuals. Send us your resume
            and let's talk!
          </p>
          <a
            href="mailto:support@geniusnexa.com"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl"
            onClick={() => trackEvent("CareerClick", "Send Resume")}
          >
            <FaEnvelope />
            Send Your Resume
          </a>
        </div>
      </div>
    </div>
  );
};

const JobPosting = ({
  title,
  location,
  type,
  experience,
  description,
  requirements,
  onClick,
}) => (
  <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all border border-gray-200 overflow-hidden group">
    <div className="p-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
            {title}
          </h3>
          <div className="flex flex-wrap gap-4 text-gray-600">
            <div className="flex items-center gap-2">
              <FaMapMarkerAlt className="text-blue-600" />
              <span>{location}</span>
            </div>
            <div className="flex items-center gap-2">
              <FaBriefcase className="text-green-600" />
              <span>{type}</span>
            </div>
            <div className="flex items-center gap-2">
              <FaGraduationCap className="text-purple-600" />
              <span>{experience}</span>
            </div>
          </div>
        </div>
      </div>

      <p className="text-gray-700 mb-6 leading-relaxed">{description}</p>

      <div className="mb-6">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <FaClock className="text-blue-600" />
          Key Requirements:
        </h4>
        <ul className="space-y-2">
          {requirements.map((requirement, index) => (
            <li key={index} className="flex items-start gap-3 text-gray-700">
              <span className="text-blue-600 mt-1">✓</span>
              <span>{requirement}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="pt-6 border-t border-gray-200">
        <p className="text-gray-700 mb-4">
          Interested in this position? We'd love to hear from you!
        </p>
        <a
          onClick={onClick}
          href={`mailto:support@geniusnexa.com?subject=Application for ${title}&body=Hi, I am interested in the ${title} position at GeniusNexa.`}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
        >
          <FaEnvelope />
          Apply Now
        </a>
      </div>
    </div>
  </div>
);

export default Careers;
